/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import { getLuxeFlags } from '../../selectors/featureFlagsSelector';
import { getCurrentStore } from '../../selectors/metaData';
import Icon from '../../atoms/Icon';
import styles from './dialog.pcss';
import noop from '../../utils/noop';
import { FLEEK_STORE } from '../../../constants/constant';
import { gtmEventPush } from '../../../modules/ga/GTMdatalayer';

const mapStateToProps = state => ({
  luxeFlags: getLuxeFlags(state),
  storeName: getCurrentStore(state),
});

const connector = connect(mapStateToProps);

const BODY_COUNTER_ATTR = 'data-dialog-trigger-count';
const TRIGGER_COUNTER_ATTR = 'data-dialog-trigger-el-count';


type Fn = (maskClick?: boolean) => void;

interface DialogProps {
  onCancel?: Fn;
  showDialog?: boolean;
  children?: ReactNode | string;
  type?: 'drawer' | 'modal';
  fullWidth?: boolean;
  maskClosable?: boolean;
  closeIcon?: boolean;
  drawerBottomCloseIcon?: boolean;
  customClass?: string;
  customContentClass?: string;
  customCloseButtonStyles?:React.CSSProperties;
  customContentWrapperStyles?:React.CSSProperties;
  isMyJioRedirection?: boolean;
  customContentWrapperClass?: string;
  videoCloseIcon?: boolean;
  customContentClassAddOns?: string;
  displayContent?: string;
  isFromPDP?: boolean;
  isFilter?: boolean;
  modalMaxHeight?: string;
  isSubscriptionModal?: boolean;
  maskStyles?: object;
  id?: string;
  enableRushDelivery?: boolean;
  isShowAddAddress?: boolean;
  opacityChange?: boolean;
  role?: string;
  ariaLevel?: number;
  isCloseIconHidden?: string;
  ariaLabel?:string;
  // if this is true then skip initial auto focus, and prevent focus trapping
  dontTreatAsDialog?:boolean
}

const cx = classNames.bind(styles);

const Dialog = (props: DialogProps): JSX.Element => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLDivElement | null>(null);
  const openButtonRef = useRef<HTMLElement | null>(null);
  const [prevOpenButton,setPrevOpenButton] = useState<HTMLElement | null>(null)
  const prevOpenButtonRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const {
    children,
    showDialog,
    onCancel,
    type = 'drawer',
    fullWidth,
    maskClosable = true,
    closeIcon = true,
    drawerBottomCloseIcon = false,
    modalStyles = {},
    customClass = '',
    luxeFlags,
    storeName,
    customContentClass,
    callbackOnVisible = noop,
    isMyJioRedirection = false,
    customContentWrapperClass = '',
    customContentWrapperStyles={},
    videoCloseIcon = false,
    customContentClassAddOns = '',
    displayContent = '',
    isFromPDP = false,
    isFilter = false, 
    modalMaxHeight,
    isSubscriptionModal = false,
    maskStyles = {},
    id = '',
    enableRushDelivery = false,
    isShowAddAddress = false,
    role,
    ariaLevel,
    opacityChange,
    ariaLabel="DialogBox",
    isCloseIconHidden = false,
    isMembershipArtical = false,
    dontTreatAsDialog=false,
    customCloseButtonStyles={}
  } = props;
  // console.log('Modal Props -> ', props)
  const { isThemeEnable } = luxeFlags;
  const luxe = isThemeEnable === 'ON' && storeName === 'luxe';
  const isFleek = storeName === FLEEK_STORE;
  const dialogClasses: string = cx(
    'dialog',
    type,
    customClass,
    { open: showDialog },
    { 'my-jio-content-wrapper': isMyJioRedirection }
  );
  const maskClasses: string = cx({ [`dialog-mask`]: true });
  const maskRushClasses: string = cx({ [`dialog-mask-rush`]: true });
  const maskMembershipClasses: string = cx({ [`dialog-mask-membership`]: true });
  const maskClassesChangedOpacity: string = cx({ [`cancel-return-container-dialog-mask`]: true });
  const contentWrapperClass: string = cx(
    {
      [customContentWrapperClass
        ? `${customContentWrapperClass}-content-wrapper`
        : `${type}-content-wrapper`]: true
    },
    { customModalWidth: fullWidth && type === 'modal' },
    { 'drawer-bottomClose-wrapper-class': drawerBottomCloseIcon }
  );
  const contentWrapperClassRushClasses: string = cx({ [`cart-address-list-content-wrapper-rush`]: true });
  const contentClass: string = cx(
    { [customContentClass ? `${customContentClass}-content` : `${type}-content`]: true },
    { [`modal-radius`]: !fullWidth && type === 'modal' },
    { 'drawer-bottomClose-class': drawerBottomCloseIcon },
    customContentClassAddOns
  );

  const isIconReq: boolean = type === 'drawer';
  const iconStyle = {
    fontSize: luxe ? '24px' : '22px',
    color: 'white',
    width: '24px'
  };

  const subscriptionIconStyle = {
    fontSize: '14px',
    color: 'white',
    width: '40px',
    height: '40px',
    borderRadius: '40px',
    background: '#000',
    boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
    textAlign: 'center',
    alignContent: 'center'
  };

  const handleGAOnCancel = () => {
    if (id.includes('return_fee_post-order')) {
      gtmEventPush({
        event: 'return_fee_halfcard_interaction',
        event_category: 'return fee halfcard interaction',
        event_action: 'cross',
        page_name: 'return fee halfcard screen'
      });
    }
  };

  const clickHandler = (): void => {
    document.body.style = null;
    handleGAOnCancel();
    onCancel();
  };

  const maskCloseHandler = (): void => {
    if (maskClosable) {
      document.body.style = null;
      handleGAOnCancel();
      onCancel(true);
    }
  };

  useEffect(()=>{
    initDialogTriggerCounter()
  },[])


  /**
 * Dialog focus management strategy:
 *
 * - A global dialog depth counter is stored on <body> using `data-dialog-trigger-count`.
 * - When a dialog opens, its trigger element is tagged with
 *   `data-dialog-trigger-el-count = currentDepth`, then the body counter is incremented.
 * - While registering a trigger, execution is skipped if the element is <body>
 *   or another dialog/popup element.
 *
 * - When a dialog closes, the current depth is read from <body> and walked downward:
 *   - The DOM is searched for an element with a matching trigger count.
 *   - If not found, the counter is decremented and the search continues.
 *   - If found but hidden or no longer in the DOM, the attribute is removed and
 *     the counter is decremented.
 *   - If found and valid, focus is restored to that element, the attribute is removed,
 *     and the counter is decremented.
 *
 * - If no valid trigger is found, no forced focus is applied.
 * - This ensures focus is restored to the most recent valid trigger and avoids
 *   focusing removed, hidden, or non-announced elements in nested dialog flows.
 */


  useEffect(() => {
    if (showDialog) {
      const originalOverflow = document.body.style.overflow;
      const originalTouchAction = document.body.style.touchAction;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      callbackOnVisible ? callbackOnVisible() : noop;
      openButtonRef.current = document.activeElement as HTMLElement;
      if(openButtonRef.current){
        openButtonRef.current.setAttribute('tabindex','-1')
      }
      if(dontTreatAsDialog == false){
        modalRef.current?.focus();
        contentRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      }
      if(openButtonRef.current){
        registerDialogTrigger(openButtonRef.current)
      }
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.touchAction = originalTouchAction;
      };
    } else {


      const [focusedAnotherDialog, el] = focusTopMostDialog()

      if(focusedAnotherDialog) return

      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';

    unregisterDialogTriggerAndFocusTrigger()
      // openButtonRef.current?.focus();
    }
  }, [showDialog]);

  useEffect(() => {
    if (!showDialog || !modalRef.current || dontTreatAsDialog) return;
    const modalElement = modalRef.current;
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleKeyDown = (event: KeyboardEvent) => {
      const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as Element);
      if (event.key === "Tab") {
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        let nextIndex = currentIndex + 1;
        if (nextIndex >= focusableElements.length) nextIndex = 0;
        focusableElements[nextIndex].focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        let prevIndex = currentIndex - 1;
        if (prevIndex < 0) prevIndex = focusableElements.length - 1;
        focusableElements[prevIndex].focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDialog]);
  // useEffect(() => {
  //   const app = document.getElementById('app');
  //   var appDesc = app?.getElementsByTagName('*') ?? [];
  //   for (let el of appDesc) {
  //     if (showDialog) {
  //       el.setAttribute('aria-hidden', 'true');
  //     } else {
  //       el.setAttribute('aria-hidden', 'false');
  //     }
  //   }

  //   var ancestors = document.querySelectorAll('#popup');
  //   for (let ancestor of ancestors) {
  //     let targetDisplay = 'block';

  //     let computedStyle = window.getComputedStyle(ancestor);
  //     if (computedStyle.display === targetDisplay) {
  //       ancestor?.setAttribute('aria-hidden', 'false');
  //       var descendents = ancestor?.getElementsByTagName('*') ?? [];
  //       for (let el of descendents) {
  //         el.setAttribute('aria-hidden', 'false');
  //       }
  //     }
  //   }
  // }, [showDialog]);

  function isElementHiddenOrNotInDOM(element) {
  // If it's not a valid HTMLElement or not connected to the DOM
  if (!(element instanceof HTMLElement) || !element.isConnected) {
    return true;
  }

  let current = element;

  while (current) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none') {
      return true;
    }
    current = current.parentElement;
  }

  return false;
}


  function focusTopMostDialog() :[boolean,HTMLElement | Element | null] {

    let didFocus = false;

    // 1. Find all potential dialog nodes using semantic attributes only
    const allDialogs = Array.from(
      document.querySelectorAll('[role="dialog"][aria-modal="true"]')
    );

    if (allDialogs.length === 0) return [false, null];

    // 2. Filter dialogs that are actually visible (display !== none)
    const visibleDialogs = allDialogs.filter(dialog => {
      const style = window.getComputedStyle(dialog);
      return style.display !== 'none';
    });

    if (visibleDialogs.length === 0) return [false, null];

    // 3. Helper to safely extract numeric z-index
    const getZIndex = el => {
      const z = window.getComputedStyle(el).zIndex;
      if (z === 'auto') return 0;
      const parsed = parseInt(z, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    // 4. Determine the top-most dialog
    let topDialog = visibleDialogs[0];
    let topZIndex = getZIndex(topDialog);

    for (let i = 1; i < visibleDialogs.length; i++) {
      const current = visibleDialogs[i];
      const currentZ = getZIndex(current);

      if (currentZ > topZIndex) {
        topDialog = current;
        topZIndex = currentZ;
      } else if (currentZ === topZIndex) {
        // Same z-index → later in DOM wins
        if (
          topDialog.compareDocumentPosition(current) &
          Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          topDialog = current;
        }
      }
    }


    const currentActiveEl = document.activeElement;

    topDialog.focus({ preventScroll: true });

    didFocus = true;
    // currentActiveEl = the element that opened the nested dialog

    registerDialogTrigger(currentActiveEl)
    return [didFocus, currentActiveEl]
  }

  function initDialogTriggerCounter(): void {
    const body = document.body;
    if (!body) return;

    if (!body.hasAttribute(BODY_COUNTER_ATTR)) {
      body.setAttribute(BODY_COUNTER_ATTR, '1');
    }
  }

  function registerDialogTrigger(el: HTMLElement): void {

    const isElDialog = el.getAttribute('role') == 'dialog' 

    if (!el || isElDialog || !document.body || el == document.body) return;

    initDialogTriggerCounter();

    const body = document.body;
    const currentCount = Number(body.getAttribute(BODY_COUNTER_ATTR)) || 1;

    el.setAttribute(TRIGGER_COUNTER_ATTR, String(currentCount));
    body.setAttribute(BODY_COUNTER_ATTR, String(currentCount + 1));
  }

  function unregisterDialogTriggerAndFocusTrigger(): void {
    const body = document.body;
    if (!body) return;

    let currentCount = Number(body.getAttribute(BODY_COUNTER_ATTR)) || 1;

    while (currentCount > 0) {
      const selector = `[${TRIGGER_COUNTER_ATTR}="${currentCount}"]`;
      const el = document.querySelector<HTMLElement>(selector);

      if (el) {
        const isHiddenOrGone = isElementHiddenOrNotInDOM(el);

        el.removeAttribute(TRIGGER_COUNTER_ATTR);
        currentCount -= 1;
        body.setAttribute(BODY_COUNTER_ATTR, String(currentCount));

        if (!isHiddenOrGone) {
          el.focus();
          return;
        }

        // element exists but is not focusable → keep walking down
        continue;
      }

      // no element for this count → step down
      currentCount -= 1;
      body.setAttribute(BODY_COUNTER_ATTR, String(currentCount));
    }
  }


  if (!showDialog && type === 'modal') {
    return <></>;
  }

  const getStyles = () => {
    // isFleek ? (isFilter ? { background: '#2E343A' } : {}) : {}
    let styleObj = {};
    if(modalMaxHeight){
      styleObj = {...styleObj, 'max-height':modalMaxHeight};
    }
    if(isFleek && (isFilter || isSubscriptionModal)){
      styleObj = {...styleObj,  background: '#2E343A'}
    }
    return styleObj;
  }

  return (
    <div
      id="popup"
      className={cx(
        dialogClasses,
        { ['topStyle-modal-rush']: enableRushDelivery },
        { ['modal-rush']: enableRushDelivery && isShowAddAddress }
      )}
      style={modalStyles}
      aria-modal={dontTreatAsDialog == false ? 'true' : 'false'}
      role={dontTreatAsDialog == false ? 'dialog' : 'none'}
      tabIndex={dontTreatAsDialog == false ? -1 : undefined}
      ref={modalRef}
      {...(props.ariaLabel !== undefined && props.ariaLabel !== '' && dontTreatAsDialog == false
        ? { 'aria-label': `${props.ariaLabel}` }
        : {})}
    >
      <div
        className={cx(
          opacityChange ? maskClassesChangedOpacity : maskClasses,
          { [maskRushClasses]: enableRushDelivery && !isShowAddAddress },
          { [maskMembershipClasses]: isMembershipArtical }
        )}
        role="presentation"
        onClick={maskCloseHandler}
      />
      <div
        className={cx(
          contentWrapperClass,
          { [contentWrapperClassRushClasses]: enableRushDelivery && !isShowAddAddress },
          {
            ['cart-address-list-content-wrapper-add-address-rush']:
              enableRushDelivery && isShowAddAddress
          }
        )}
        style={{
          ...customContentWrapperStyles
        }}
      >
        {videoCloseIcon && !drawerBottomCloseIcon && (
          <div
            id="popup_close"
            className={cx('video-close')}
            role="button"
            onClick={clickHandler}
            // tabIndex={0}
            {...(isCloseIconHidden ? { 'aria-hidden': 'true' } : { 'aria-label': 'close' })}
          >
            <Icon icon="icon-Close" styles={iconStyle} ariaHidden />
          </div>
        )}
        {isIconReq && closeIcon && !drawerBottomCloseIcon && (
          <div
            id="popup_close"
            className={cx('close')}
            role="button"
            onClick={clickHandler}
            ref={closeButtonRef}
            style={{
              padding:"0px 26px 16px 0px",
              marginLeft:'2px',
              ...customCloseButtonStyles
            }}
            tabIndex={isCloseIconHidden ? -1 : 0}
            {...(isCloseIconHidden ? { 'aria-hidden': 'true' } : { 'aria-label': 'close' })}
          >
            <Icon icon="icon-Close-2" styles={iconStyle} ariaHidden />
          </div>
        )}
        <div
          className={cx(
            contentClass,
            { ['cart-address-list-dialog-content-rush']: enableRushDelivery },
            { ['cart-address-list-dialog-content-fleek-rush']: enableRushDelivery && isFleek },
            {
              ['cart-address-list-dialog-content-add-address-rush']:
                enableRushDelivery && isShowAddAddress
            }
          )}
          style={getStyles()}
          ref={contentRef}
          role={role}
          aria-level={ariaLevel}
          // tabIndex={0}
        >
          {children}
        </div>
        {isIconReq && closeIcon && drawerBottomCloseIcon && !isCloseIconHidden && (
          <div
            id="popup_close"
            className={cx('bottom-close')}
            role="presentation"
            onClick={clickHandler}
            // tabIndex={0}
          >
            <Icon icon="icon-Close-2" styles={iconStyle} ariaLabel="close" ariaHidden={isCloseIconHidden ? true : undefined}/>
          </div>
        )}

        {isMembershipArtical && (
          <div
            id="popup_close"
            className={cx('bottom-close')}
            role="button"
            onClick={clickHandler}
            tabIndex={0}
            aria-label='close'
          >
            <Icon icon="icon-Close" styles={subscriptionIconStyle} ariaHidden />
          </div>
        )}
      </div>
    </div>
  );
};

export default connector(Dialog);
